/*
  V1C P3: cart drawer behaviour (snippets/v1-cart-drawer.liquid, v1-cart-line.liquid). Loaded on every page
  from snippets/minicart.liquid. theme.js is not modified.

  - Event delegation on document: the drawer content (#minicart-form) is replaced after every change,
    by theme.js (Add to cart) and by this file, so nothing is bound to the drawer elements themselves.
  - Every change asks the cart endpoint for the "minicart-form" section in the same request
    (Section Rendering API) and swaps #minicart-form's content, exactly like theme.js does.
  - Variant swap (size / colour dropdown): one POST /cart/update.js, old line key -> 0 and new variant -> same
    quantity (or the existing line of that variant -> its quantity + the moved quantity). If the new variant
    is not in the returned cart, it is added with /cart/add.js (fallback).
    The same request saves the display order (cart attribute _v1_order, variant IDs top to bottom, the new
    variant in the old line's place); snippets/v1-cart-drawer.liquid renders lines in that order.
  - Loading: the changed line gets .v1-cart-line--busy (50% + spinner on its photo), the drawer gets
    .v1-cart--busy (express buttons 50% + no clicks), Checkout is disabled. Other lines stay active.
  - Changes run one after another (queue), so two quick taps never race.
  - 3rd-polo block (snippets/v1-cart-third-polo.liquid): a tile tap only selects (no request) and rewrites the
    button text from its data-template; the button adds that variant (qty 1) with /cart/add.js + the section.
    While adding: block .v1-cart-third--busy (50%, no clicks) + the same drawer busy state.
*/
(function () {
  'use strict';

  var SECTION = 'minicart-form';
  var queue = Promise.resolve();
  var pending = 0;

  function drawer() {
    return document.querySelector('[data-v1-cart]');
  }

  function setBusy(el, on, cls) {
    if (el) el.classList.toggle(cls || 'v1-cart-line--busy', on);
    pending += on ? 1 : -1;
    var root = drawer();
    if (!root) return;
    var busy = pending > 0;
    root.classList.toggle('v1-cart--busy', busy);
    var checkout = root.querySelector('[data-v1-checkout]');
    if (checkout) checkout.disabled = busy;
  }

  // Header icon counters (same as theme.js after a change)
  function updateCounts(count) {
    if (count == null) return;
    document.querySelectorAll('.cart-count').forEach(function (el) {
      if (el.classList.contains('cart-count-drawer')) el.textContent = '(' + count + ')';
      else el.textContent = count > 100 ? '~' : count;
    });
  }

  function renderSection(html) {
    var target = document.getElementById(SECTION);
    if (!html || !target) return false;
    var source = new DOMParser().parseFromString(html, 'text/html').getElementById(SECTION);
    if (!source) return false;
    target.innerHTML = source.innerHTML;
    // Busy state survives a re-render while other changes are still running
    var root = drawer();
    if (root && pending > 0) {
      root.classList.add('v1-cart--busy');
      var checkout = root.querySelector('[data-v1-checkout]');
      if (checkout) checkout.disabled = true;
    }
    document.dispatchEvent(new CustomEvent('v1:cart:updated'));
    return true;
  }

  // Full refresh (after an error or when a response has no section)
  function refresh() {
    return Promise.all([
      fetch(window.location.pathname + '?sections=' + SECTION).then(function (r) {
        return r.json();
      }),
      fetch('/cart.js').then(function (r) {
        return r.json();
      }),
    ]).then(function (res) {
      renderSection(res[0] && res[0][SECTION]);
      updateCounts(res[1].item_count);
    });
  }

  function post(url, body) {
    body.sections = [SECTION];
    body.sections_url = window.location.pathname;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.json().then(function (json) {
        if (!r.ok || json.status) throw json;
        return json;
      });
    });
  }

  // Run one cart change at a time; the line shows its loading state meanwhile.
  function run(line, task, cls) {
    setBusy(line, true, cls);
    queue = queue
      .then(task)
      .catch(function (err) {
        if (window.console) console.warn('[v1-cart] cart update failed', err);
        return refresh();
      })
      .then(function () {
        setBusy(line, false, cls);
      });
  }

  function cartLines() {
    var el = document.querySelector('[data-v1-cart-lines]');
    try {
      return el ? JSON.parse(el.textContent) : [];
    } catch (e) {
      return [];
    }
  }

  // ---------- Quantity and remove ----------

  function changeQuantity(line, quantity) {
    var key = line.getAttribute('data-key');
    run(line, function () {
      return post('/cart/change.js', { id: key, quantity: quantity }).then(function (cart) {
        if (!renderSection(cart.sections && cart.sections[SECTION])) return refresh();
        updateCounts(cart.item_count);
      });
    });
  }

  // ---------- Variant swap ----------

  function swapVariant(line, select) {
    var data;
    try {
      data = JSON.parse(line.querySelector('[data-v1-variants]').textContent);
    } catch (e) {
      return;
    }
    var currentId = Number(line.getAttribute('data-variant'));
    var current = data.find(function (v) {
      return v[0] === currentId;
    });
    if (!current) return;
    var options = current[1].slice();
    line.querySelectorAll('[data-v1-option]').forEach(function (s) {
      options[Number(s.getAttribute('data-index'))] = s.value;
    });
    var next = data.find(function (v) {
      return v[1].every(function (o, i) {
        return o === options[i];
      });
    });
    if (!next || !next[2] || next[0] === currentId) {
      select.value = select.getAttribute('data-current');
      return;
    }
    var key = line.getAttribute('data-key');
    var quantity = Number(line.getAttribute('data-qty')) || 1;
    var existing = cartLines().find(function (l) {
      return l.id === next[0] && l.key !== key;
    });
    var updates = {};
    updates[key] = 0;
    if (existing) updates[existing.key] = existing.qty + quantity;
    else updates[next[0]] = quantity;

    // Stable display order: the new variant takes the old line's place (or, when it merges into a line that is
    // already in the cart, the old line just leaves the list). Saved in the same request as a hidden attribute.
    var order = [];
    document.querySelectorAll('[data-v1-cart] [data-v1-line]').forEach(function (l) {
      var id = l.getAttribute('data-variant');
      if (l === line) id = existing ? '' : String(next[0]);
      if (id && order.indexOf(id) === -1) order.push(id);
    });

    run(line, function () {
      return post('/cart/update.js', { updates: updates, attributes: { _v1_order: order.join(',') } }).then(function (cart) {
        var added = (cart.items || []).some(function (i) {
          return i.variant_id === next[0];
        });
        if (added) {
          if (!renderSection(cart.sections && cart.sections[SECTION])) return refresh();
          updateCounts(cart.item_count);
          return;
        }
        // Fallback: the old line is already gone, add the new variant.
        return post('/cart/add.js', { items: [{ id: next[0], quantity: quantity }] }).then(function (res) {
          if (!renderSection(res.sections && res.sections[SECTION])) return refresh();
          return fetch('/cart.js')
            .then(function (r) {
              return r.json();
            })
            .then(function (c) {
              updateCounts(c.item_count);
            });
        });
      });
    });
  }

  // ---------- 3rd-polo block ----------

  function handleThird(e, block) {
    if (block.classList.contains('v1-cart-third--busy')) return;
    var tile = e.target.closest('[data-v1-tile]');
    if (tile) {
      e.preventDefault();
      if (tile.disabled) return;
      block.querySelectorAll('[data-v1-tile]').forEach(function (t) {
        var on = t === tile;
        t.classList.toggle('is-selected', on);
        t.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      var button = block.querySelector('[data-v1-third-add]');
      button.setAttribute('data-variant', tile.getAttribute('data-variant'));
      button.textContent = (block.getAttribute('data-template') || '')
        .replace('[colour]', tile.getAttribute('data-colour'))
        .replace('[size]', block.getAttribute('data-size'));
      return;
    }
    var add = e.target.closest('[data-v1-third-add]');
    if (!add) return;
    e.preventDefault();
    var id = Number(add.getAttribute('data-variant'));
    if (!id) return;
    add.disabled = true;
    run(
      block,
      function () {
        return post('/cart/add.js', { items: [{ id: id, quantity: 1 }] }).then(function (res) {
          if (!renderSection(res.sections && res.sections[SECTION])) return refresh();
          return fetch('/cart.js')
            .then(function (r) {
              return r.json();
            })
            .then(function (c) {
              updateCounts(c.item_count);
            });
        });
      },
      'v1-cart-third--busy'
    );
  }

  // ---------- Events (delegated) ----------

  document.addEventListener('click', function (e) {
    var root = e.target.closest('[data-v1-cart]');
    if (!root) return;

    if (e.target.closest('[data-v1-cart-close]')) {
      e.preventDefault();
      var notification = document.querySelector('cart-notification');
      if (notification && typeof notification.close === 'function') notification.close();
      return;
    }

    var third = e.target.closest('[data-v1-third]');
    if (third) {
      handleThird(e, third);
      return;
    }

    var line = e.target.closest('[data-v1-line]');
    if (!line || line.classList.contains('v1-cart-line--busy')) return;

    var qtyButton = e.target.closest('[data-v1-qty]');
    if (qtyButton) {
      e.preventDefault();
      var next = (Number(line.getAttribute('data-qty')) || 0) + Number(qtyButton.getAttribute('data-v1-qty'));
      changeQuantity(line, Math.max(0, next));
      return;
    }

    if (e.target.closest('[data-v1-remove]')) {
      e.preventDefault();
      changeQuantity(line, 0);
    }
  });

  document.addEventListener('change', function (e) {
    var select = e.target.closest('[data-v1-cart] [data-v1-option]');
    if (!select) return;
    var line = select.closest('[data-v1-line]');
    if (!line || line.classList.contains('v1-cart-line--busy')) {
      select.value = select.getAttribute('data-current');
      return;
    }
    swapVariant(line, select);
  });
})();
